import type { LegacyRef } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import type WebView from 'react-native-webview';
import { theme, useResponsive } from '@sweetgreen/garnish';

import { CreditCardFormControlledContent } from './components/CreditCardForm.controlled';
import { defaultCreditCardForm } from './CreditCardForm.constants';
import type { FormProps } from './CreditCardForm.types';
import { useBraintreeMessages } from './hooks/Braintree.hooks';
import { useCreditCardTelemetry } from './hooks/CreditCardForm.hooks.telemetry';
import { FRAME_HEIGHT } from './hosted-fields/constants';
import { useBraintreeWebSource } from './hosted-fields/CreditCardForm.webView';
import {
  CreditCardFormHost,
  injectTokenizeRequest,
} from './hosted-fields/CreditCardFormHost';

export const CreditCardFormContent = ({
  vault,
  account,
  subtitle,
  isSaving,
  isEditing,
  isDeleting,
  onSave,
  onEdit,
  onDelete,
}: FormProps) => {
  const { match } = useResponsive();

  // ────── Controlled Form State ───────────────────────────────────────
  const [form, setForm] = useState({
    nickname: account?.nickname ?? defaultCreditCardForm.nickname,
    default: account?.isDefault ?? defaultCreditCardForm.default,
  });

  // ────── Telemetry ───────────────────────────────────────────────────
  const {
    monitoredOnSave,
    monitoredOnEdit,
    monitoredOnFail,
    monitoredOnDelete,
  } = useCreditCardTelemetry({
    isEditing,
    onSave,
    onEdit,
    onDelete,
  });

  // ────── WebView Setup ───────────────────────────────────────────────
  const braintreeWebSource = useBraintreeWebSource(vault);
  const webViewRef = useRef<WebView>();
  const {
    nonce,
    isLoadingFields,
    isTokenizing,
    canSubmit,
    handleBraintreeMessages,
  } = useBraintreeMessages({ isEditing, onError: monitoredOnFail });

  // ────── Submitting New Card ─────────────────────────────────────────
  const saveReference = useRef<typeof monitoredOnSave>();

  // eslint-disable-next-line functional/immutable-data
  saveReference.current = monitoredOnSave;

  useEffect(() => {
    if (nonce) saveReference.current?.(form, nonce);
  }, [nonce, form]);

  // ─────────────────────── Callbacks ──────────────────────────────────
  const submitForm = useCallback(() => {
    (async () => {
      if (isEditing) {
        monitoredOnEdit(form);

        return;
      }

      injectTokenizeRequest(webViewRef);
    })();
  }, [isEditing, form, monitoredOnEdit]);

  return (
    <ScrollView
      testID="credit-card-form"
      contentContainerStyle={styles.content}
    >
      <CreditCardFormControlledContent
        account={account}
        subtitle={subtitle}
        isSaving={Boolean(isSaving) || isTokenizing}
        isEditing={isEditing}
        isDeleting={isDeleting}
        isLoadingFields={isLoadingFields}
        canSubmitForm={canSubmit}
        form={form}
        fadeDuration={0}
        onFormChange={setForm}
        onSubmitForm={submitForm}
        onDelete={monitoredOnDelete}
      >
        {isEditing ? null : (
          <CreditCardFormHost
            ref={webViewRef as unknown as LegacyRef<WebView>}
            isLoadingFields={isLoadingFields}
            source={braintreeWebSource}
            onMessage={handleBraintreeMessages}
            containerStyle={[
              styles.container,
              match([styles.paddingSmall, styles.paddingMedium]),
              isLoadingFields && styles.loadingFields,
            ]}
          />
        )}
      </CreditCardFormControlledContent>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: FRAME_HEIGHT,
    backgroundColor: theme.colors.OATMEAL,
  },
  paddingSmall: {
    paddingHorizontal: theme.spacing.small,
  },
  paddingMedium: {
    paddingHorizontal: theme.spacing.medium,
  },
  // for web views to function properly on Android, their container must be at least 1x1 in size
  loadingFields: {
    flex: 0,
    width: 1,
    height: 1,
  },
  content: {
    backgroundColor: theme.colors.OATMEAL,
  },
});
